import * as React from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { useNavigate } from 'react-router-dom';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';

import Routes from '../../../../services/Routes';

import { OptionFactory } from './services/OptionFactory';
import { AUTOCOMPLETE } from './queries';
import { IAutocompleteQuery, IAutocompleteQueryVariables } from './queries.types';
import { IExtraOption, ITypeaheadOption } from './types';

interface Props {
  className: string;
  onBlur?: () => void;
  onFocus?: () => void;
}

const minLength = 4;

export const SearchBar = ({ className, onBlur, onFocus }: Props) => {
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [text, setText] = React.useState('');
  const [extraOptions, setExtraOptions] = React.useState<IExtraOption[]>([]);

  const [doQuery, { data, loading }] = useLazyQuery<
    IAutocompleteQuery,
    IAutocompleteQueryVariables
  >(AUTOCOMPLETE, { fetchPolicy: 'no-cache' });

  const callApi = React.useCallback(
    (query: string) => {
      doQuery({
        variables: {
          query,
          limit: 20,
        },
      });
    },
    [doQuery]
  );

  const handleKeydown = React.useCallback(
    (e) => {
      // Check whether the 'enter' key was pressed, and also make sure that
      // no menu items are highlighted.
      if (e.keyCode === 13 && activeIndex === -1 && text.length >= minLength) {
        // go to search page on enter
        navigate(Routes.search({ query: text }));
      }
    },
    [text, activeIndex]
  );

  const handleSearch = React.useCallback((query: string) => {
    // If value is a number
    if (query && +query === +query) {
      setExtraOptions([
        { __typename: 'fastOrder', id: query },
        { __typename: 'fastCustomer', id: query },
      ]);
    } else {
      setExtraOptions([]);
    }

    callApi(query);
    setText(query);
  }, []);

  const handleOptionSelected = React.useCallback(
    (items: Array<ITypeaheadOption>) => {
      if (items.length) {
        const item = items[0];

        switch (item.__typename) {
          case 'fastCustomer':
          case 'IndexedCustomer':
            navigate(Routes.customer(item.id));
            break;
          case 'fastOrder':
          case 'IndexedOrder':
            navigate(Routes.order(item.id));
            break;
          default:
        }
      }
    },
    [navigate]
  );

  const handleLabel = React.useCallback((item: ITypeaheadOption) => {
    switch (item.__typename) {
      case 'fastCustomer':
        return `Customer: ${item.id}`;
      case 'IndexedCustomer':
        return `Customer: ${item.name || item.id}`;
      case 'fastOrder':
      case 'IndexedOrder':
        return `Order: #${item.id}`;
      default:
        return '';
    }
  }, []);

  // eslint-disable-next-line max-len
  const options: ITypeaheadOption[] = [...extraOptions, ...(data?.globalAutocomplete?.items || [])];

  return (
    <AsyncTypeahead
      id="search"
      delay={500}
      filterBy={() => true /* WTH */}
      inputProps={{ className }}
      isLoading={loading}
      labelKey={handleLabel}
      // @ts-ignore the @types for the Typeahead do not include the maxHeight property but it works and is passed to the Menu.
      maxHeight="80vh"
      minLength={minLength}
      options={options}
      placeholder="Search customers, orders, etc..."
      renderMenuItemChildren={(option) => <OptionFactory item={option} />}
      onBlur={() => onBlur && onBlur()}
      onChange={handleOptionSelected}
      onFocus={() => onFocus && onFocus()}
      onKeyDown={handleKeydown}
      onSearch={handleSearch}
    >
      {(state: { activeIndex: number }) => (
        // Passing a child render function to the component exposes partial
        // internal state, including the index of the highlighted menu item.
        // https://stackoverflow.com/questions/63275021/react-bootstrap-typeahead-handling-enter-key-if-no-menu-item-selected
        <ActiveIndexWatcher update={() => setActiveIndex(state.activeIndex)} />
      )}
    </AsyncTypeahead>
  );
};

const ActiveIndexWatcher = ({ update }: { update: () => void }): null => {
  React.useEffect(update);
  return null;
};
